import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Keyboard, Mic, Users, MessageSquareText, BarChart3, Lock } from "lucide-react";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";
import { useSeo, SITE_URL } from "../../lib/seo";
import { MonthlyPrice } from "../../lib/pricing";

// Public conversion landing page for the Mock Oral Board. Marketing only: every CTA goes through the EXISTING
// signup/login flow (/auth) or the EXISTING protected /interview route — the server's entitlement rules decide what a
// user can start. Nothing on this page grants, resets or bypasses access.
const PATH = "/firefighter-mock-oral-board";
const PANEL = [
  { name: "Captain Reyes", img: "/panel/reyes.jpg" },
  { name: "Battalion Chief Okafor", img: "/panel/okafor.jpg" },
  { name: "Fire Marshal Bennett", img: "/panel/bennett.jpg" },
];
const FAQS = [
  ["Is the first Mock Oral Board really free?", "Yes. A free First Step account includes one full general Mock Oral Board — 6–10 questions with the three-person AI panel, answered by voice or by typing — with a summary report. No credit card is required. It is one board per account and does not renew."],
  ["Is the free board department-specific?", "No. The free board is a general firefighter oral board. Department-focused Mock Oral Boards and the detailed graded report are part of Premium."],
  ["How does the feedback work?", "The same evaluation runs on every board: your answers are scored on content categories such as communication, motivation, judgment, integrity, teamwork and accountability, and your delivery is assessed separately. Free accounts receive a summary report; Premium members receive the full detailed report."],
  ["What does Premium include?", "15 Mock Oral Boards a month (general or department-focused, voice or typed) with full graded reports, plus the full study question bank, the researched-department library, Resume Builder PDF and AI review, and the Fitness Plan."],
];

function Cta({ id, size = "lg", className = "" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Logged out → existing signup. Logged in → existing protected Mock Oral Board page (server decides eligibility).
  const go = () => navigate(user ? "/interview" : "/auth?mode=register");
  return (
    <div className={className}>
      <Button onClick={go} size={size} className="bg-primary hover:bg-primary/90 text-white h-auto min-h-[3.25rem] py-3 px-6 sm:px-8 text-base font-semibold group w-full sm:w-auto whitespace-normal text-center" data-testid={id}>
        Try Your First Full Mock Oral Board Free <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </Button>
      <p className="mt-3 text-sm text-slate-400" data-testid={`${id}-note`}>First full general Mock Oral Board free — no credit card required.</p>
    </div>
  );
}

function PanelPreview() {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 shadow-2xl" data-testid="mob-panel-preview" aria-label="Preview of the Mock Oral Board panel (illustration, not interactive)">
      <img src="/panel/room_bg.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="relative p-5 sm:p-7">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400">Your panel</p>
        <div className="mt-4 grid grid-cols-3 gap-3 sm:gap-5">
          {PANEL.map((p) => (
            <figure key={p.name} className="text-center">
              <img src={p.img} alt={`${p.name}, AI panelist`} className="w-full aspect-square object-cover rounded-xl border-2 border-slate-700" loading="lazy" />
              <figcaption className="mt-2 text-xs sm:text-sm font-semibold text-white">{p.name}</figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-5 rounded-xl bg-slate-950/80 border border-slate-700 p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Captain Reyes asks</p>
          <p className="mt-1 text-white leading-relaxed">“Tell us about a time you had a conflict with a coworker. How did you handle it, and what would you do differently today?”</p>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold" aria-hidden="true">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 text-slate-200 px-3 py-1.5"><Mic className="w-3.5 h-3.5 text-amber-400" /> Answer by voice</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 text-slate-200 px-3 py-1.5"><Keyboard className="w-3.5 h-3.5 text-amber-400" /> or type your answer</span>
        </div>
        <p className="mt-3 text-[11px] text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Preview only — sign in to start a real board.</p>
      </div>
    </div>
  );
}

export default function FirefighterMockOralBoard() {
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Firefighter Mock Oral Board", item: `${SITE_URL}${PATH}` }] },
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
  ];
  useSeo({ title: "Firefighter Mock Oral Board — Practice Your Interview With an AI Panel", description: "Practice a realistic firefighter mock oral board with a three-person AI panel. Answer by voice or typing and get feedback on your answers and delivery. Your first full general board is free — no credit card required.", path: PATH, jsonLd });

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="mock-oral-board-landing">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-slate-950 tactical-grid">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400">Firefighter Mock Oral Board</p>
              <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-none text-white text-balance" data-testid="mob-h1">
                Practice your firefighter oral board <span className="text-primary">before the real thing.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
                Sit in front of a realistic three-person AI panel, answer real firefighter oral-board questions by voice or by typing, and get feedback on what you said and how you delivered it.
              </p>
              <Cta id="mob-cta-hero" className="mt-8" />
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-400 text-sm">
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> Full 6–10 question board</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> Voice or typed answers</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> Scored feedback</span>
              </div>
            </div>
            <PanelPreview />
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="mob-how-it-works">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">How it works</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-slate-900 uppercase tracking-tight">A real board, not a question list</h2>
          <div className="mt-10 grid md:grid-cols-4 gap-6">
            {[[Users, "Face the panel", "Three AI panelists with distinct voices take turns asking questions and follow-ups, the way a real board does."],
              [MessageSquareText, "Get real questions", "Motivation, situational judgment, ethics, teamwork, conflict and department knowledge — the categories panels actually score."],
              [Mic, "Answer out loud or type", "Speak your answers hands-free (recommended) or type them. Either way it's one continuous board."],
              [BarChart3, "See your feedback", "Answers are scored by category and your delivery is assessed separately — Free accounts get a summary, Premium gets the full detailed report."]].map(([Icon, t, d]) => (
              <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-primary/40 transition-colors">
                <Icon className="w-6 h-6 text-primary" />
                <h3 className="mt-4 font-heading text-xl font-bold text-slate-900">{t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 bg-slate-950 rounded-2xl p-8 sm:p-10 text-white">
            <p className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">Try it with your own answers</p>
            <Cta id="mob-cta-mid" className="mt-6" />
          </div>
        </section>

        {/* Free → Premium */}
        <section className="bg-slate-50 border-y border-slate-200" data-testid="mob-free-premium">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">Start free, go further with Premium</p>
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="mob-free-card">
                <p className="uppercase text-xs tracking-widest text-slate-400 font-semibold">Start Free</p>
                <p className="font-heading text-4xl font-extrabold text-slate-900 mt-3">$0</p>
                <ul className="mt-6 space-y-3 text-sm text-slate-600">
                  {["One full general Mock Oral Board (6–10 questions) with the three-person AI panel", "Answer by voice or by typing", "Summary report: overall and category scores, two strengths, two things to improve", "No credit card required · one board per account, does not renew", "Also free: 50 study questions a day per track, Resume Builder, Career Road Map"].map((f) => (
                    <li key={f} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-slate-400 shrink-0" />{f}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-gradient-to-b from-slate-900 to-slate-950 border-2 border-primary p-8 text-white" data-testid="mob-premium-card">
                <p className="uppercase text-xs tracking-widest text-amber-400 font-semibold">Continue With Premium</p>
                <p className="font-heading text-4xl font-extrabold mt-3" data-testid="mob-premium-price"><MonthlyPrice suffix="/month" /></p>
                <p className="text-slate-400 text-sm mt-1">or $200/year · cancel anytime</p>
                <ul className="mt-6 space-y-3 text-sm text-slate-200">
                  {["15 Mock Oral Boards a month — general or department-focused, voice or typed", "Full detailed graded report on every board, including per-question feedback and delivery", "Full question bank, researched-department library, Resume PDF & AI review, Fitness Plan"].map((f) => (
                    <li key={f} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" />{f}</li>
                  ))}
                </ul>
                <Link to="/pricing" className="mt-6 inline-flex text-sm font-semibold text-slate-200 hover:text-white underline underline-offset-2" data-testid="mob-pricing-link">See full pricing</Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ + final CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12" data-testid="mob-faq">
          <div>
            <h2 className="font-heading text-3xl font-bold text-slate-900 uppercase tracking-tight">Questions candidates ask</h2>
            <dl className="mt-6 divide-y divide-slate-200">
              {FAQS.map(([q, a]) => (
                <div key={q} className="py-4"><dt className="font-semibold text-slate-900">{q}</dt><dd className="mt-1 text-sm text-slate-600 leading-relaxed">{a}</dd></div>
              ))}
            </dl>
            <p className="mt-6 text-sm text-slate-500">Want to study the format first? Read our guides on <Link to="/firefighter-oral-board-prep" className="text-primary hover:underline">oral board preparation</Link>, <Link to="/firefighter-oral-board-questions" className="text-primary hover:underline">common oral board questions</Link> and <Link to="/firefighter-interview-practice" className="text-primary hover:underline">interview practice</Link>. Still ahead of the written exam? Take the free <Link to="/firefighter-practice-test" className="text-primary hover:underline">firefighter practice test</Link>.</p>
          </div>
          <div className="bg-slate-950 rounded-2xl p-8 sm:p-10 text-white self-start lg:sticky lg:top-24">
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-400">Ready when you are</p>
            <p className="mt-3 font-heading text-3xl font-bold uppercase tracking-tight">Walk into your oral board already prepared.</p>
            <Cta id="mob-cta-bottom" className="mt-6" />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
